import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Libellé du module protégé, pour message d'erreur explicite */
  moduleLabel?: string;
}

interface State {
  hasError:      boolean;
  isChunkError:  boolean;
  errorMessage:  string;
}

/**
 * ErrorBoundary spécialisé pour les routes lazy (React.lazy + Suspense).
 *
 * Ticket SIMGROUP-2026-002 B-01. Cas typique en production :
 *   1. Un utilisateur ouvre la SPA (bundle daté du jour J)
 *   2. Le PDG déploie une nouvelle version (jour J+1) — les anciens chunks
 *      hashés sont supprimés de /assets/
 *   3. L'utilisateur navigue vers une route lazy → import du chunk J → 404
 *      (avec fix nginx, sinon HTML fallback → erreur MIME)
 *
 * Sans ErrorBoundary : React efface tout le DOM, écran blanc total, sidebar
 * comprise. Impossible de récupérer sans F5.
 *
 * Avec ce composant : la sidebar reste, on affiche un message clair et un
 * bouton "Recharger la page" qui vide le cache + service worker avant
 * de recharger.
 */
export class LazyErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, errorMessage: '' };

  static getDerivedStateFromError(error: Error): State {
    const msg = (error?.message ?? String(error)).toLowerCase();
    // Détecte les erreurs typiques d'import dynamique en échec :
    //   - "Failed to fetch dynamically imported module"
    //   - "Failed to load module script" / MIME type
    //   - "ChunkLoadError" (webpack) / "Loading chunk N failed" (vite via rollup)
    //   - "importing a module script failed"
    const isChunkError =
      msg.includes('dynamically imported module') ||
      msg.includes('failed to fetch') ||
      msg.includes('module script') ||
      msg.includes('mime type') ||
      msg.includes('chunkloaderror') ||
      msg.includes('loading chunk') ||
      msg.includes('importing a module');
    return {
      hasError: true,
      isChunkError,
      errorMessage: error?.message ?? 'Erreur inconnue',
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Trace console pour débug (les erreurs sont aussi disponibles côté Sentry
    // le jour où on l'intègre).
    console.error('[LazyErrorBoundary]', error, info?.componentStack);
  }

  private handleReload = async () => {
    // 1. Unregister tous les service workers pour forcer récupération du nouvel index.html
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* pas bloquant */ }
    // 2. Vide les caches PWA
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* pas bloquant */ }
    // 3. Reload dur (bypass HTTP cache)
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isChunkError, errorMessage } = this.state;
    const label = this.props.moduleLabel ?? 'ce module';

    return (
      <div className="ds-content">
        <div
          className="ds-panel"
          style={{
            padding:      32,
            textAlign:    'center',
            maxWidth:     560,
            margin:       '40px auto',
            border:       '1px solid var(--warning)',
            borderRadius: 8,
          }}
        >
          <AlertTriangle size={36} style={{ margin: '0 auto 12px', color: 'var(--warning)' }} />
          <h2 style={{ fontSize: 18, color: 'var(--tx)', marginBottom: 8 }}>
            {isChunkError
              ? 'Une nouvelle version est disponible'
              : `Le chargement de ${label} a échoué`}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--tx-2)', marginBottom: 20, lineHeight: 1.5 }}>
            {isChunkError ? (
              <>
                L'application a été mise à jour depuis votre dernière visite.
                Rechargez la page pour récupérer la dernière version.
              </>
            ) : (
              <>
                Le module n'a pas pu s'ouvrir. Le reste de l'application reste utilisable
                — vous pouvez naviguer ailleurs via le menu latéral.
              </>
            )}
          </p>
          <button
            type="button"
            onClick={() => void this.handleReload()}
            className="ds-btn ds-btn-p"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={14} /> Recharger la page
          </button>
          <details style={{ marginTop: 20, textAlign: 'left', fontSize: 11, color: 'var(--tx-3)' }}>
            <summary style={{ cursor: 'pointer' }}>Détails techniques</summary>
            <pre style={{
              marginTop: 8, padding: 8, background: 'var(--bg-2)',
              borderRadius: 4, overflowX: 'auto', whiteSpace: 'pre-wrap',
            }}>
              {errorMessage}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
